import { useMutation } from "@tanstack/react-query";
import { useSession } from "@/services/auth0/session";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

export interface PreviewPickupQuoteInput {
  carrierServiceId: string;
  carrierAddOnServiceId: string;
  dispatchCenterId: string;
  destinationCarrierId: string;
  fromServiceAreaId: string;
  toServiceAreaId: string;
  /** Actual package weight in kg — engine picks max(actualKg, dimKg) per leg. */
  actualWeightKg: number;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
}

export interface PreviewPickupQuoteResult {
  carrierServiceExists: boolean;
  /** Billable kg the label leg priced against. */
  labelBillableKg: number;
  /** Billable kg the pickup + redist legs priced against. */
  pickupBillableKg: number;
  labelLegCents: number | null;
  pickupLegCents: number | null;
  redistLegCents: number | null;
  /** Pickup leg + redist leg. What the customer pays for pickup. */
  totalPickupCents: number | null;
  labelMissing: boolean;
  pickupError: { code: string; message: string } | null;
}

/**
 * Calls the admin quote-preview endpoint — same engine the customer quote
 * uses, scoped to a single (lane, package shape) tuple. Returns per-leg
 * billable kg so admin can see when label and pickup diverge on low-density
 * boxes.
 */
export function usePreviewPickupQuote() {
  const { token } = useSession();

  return useMutation({
    mutationFn: async (
      input: PreviewPickupQuoteInput
    ): Promise<PreviewPickupQuoteResult> => {
      const { data } = await axios.post<{
        success: boolean;
        data: PreviewPickupQuoteResult;
      }>(`${API_URL}/api/v2/admin/pricing/preview-pickup-quote`, input, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data.data;
    },
  });
}
